import { useState, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  medicalRecordsApi,
  personnelApi,
  pdvApi,
  ocrApi,
  personnelReportsApi as reportsApi,
} from '../lib/api';
import { toast } from 'sonner';
import {
  Stethoscope,
  Upload,
  Download,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Search,
  FileText,
  Sparkles,
  FileSpreadsheet,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';

const statusBadge = (status, days) => {
  if (status === 'expired')
    return <Badge className="bg-red-100 text-red-800 border border-red-300">🔴 Scaduta {days != null && `(${Math.abs(days)}g fa)`}</Badge>;
  if (status === 'expiring_soon')
    return <Badge className="bg-amber-100 text-amber-800 border border-amber-300">🟡 Scade tra {days}g</Badge>;
  if (status === 'no_expiry')
    return <Badge className="bg-gray-100 text-gray-700 border">⚪ Senza scadenza</Badge>;
  return <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300">🟢 Valida {days != null && `(${days}g)`}</Badge>;
};

const initialForm = {
  personnel_id: '',
  visit_date: new Date().toISOString().split('T')[0],
  next_visit_date: '',
  doctor_name: '',
  judgment: '',
  notes: '',
  file: null,
};

export default function MedicalRecordsPage() {
  const [records, setRecords] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [pdvs, setPdvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [pdvFilter, setPdvFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [form, setForm] = useState(initialForm);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      setLoading(true);
      const [rRes, pRes, pdvRes] = await Promise.all([
        medicalRecordsApi.getAll(),
        personnelApi.getAll().catch(() => ({ data: [] })),
        pdvApi.getAll().catch(() => ({ data: [] })),
      ]);
      setRecords(rRes.data);
      setPersonnel(Array.isArray(pRes.data) ? pRes.data : pRes.data.items || []);
      setPdvs(Array.isArray(pdvRes.data) ? pdvRes.data : pdvRes.data.items || []);
    } catch {
      toast.error('Errore caricamento visite mediche');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return records.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (pdvFilter !== 'all' && r.pdv_id !== pdvFilter) return false;
      if (q) {
        const hay = `${r.personnel_name || ''} ${r.personnel_fiscal_code || ''} ${r.doctor_name || ''} ${r.pdv_name || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [records, statusFilter, pdvFilter, search]);

  const stats = useMemo(() => ({
    total: records.length,
    expired: records.filter((r) => r.status === 'expired').length,
    expiring: records.filter((r) => r.status === 'expiring_soon').length,
    valid: records.filter((r) => r.status === 'valid').length,
  }), [records]);

  // OCR auto-fill
  const handleFileChange = async (file) => {
    setForm((f) => ({ ...f, file }));
    if (!file) return;
    const ext = file.name.toLowerCase().split('.').pop();
    if (!['pdf', 'jpg', 'jpeg', 'png'].includes(ext)) return;
    try {
      setOcrLoading(true);
      const res = await ocrApi.extract(file, 'medical_visit');
      const e = res.data?.extracted || {};
      const matched = res.data?.matched_personnel;
      setForm((f) => ({
        ...f,
        personnel_id: matched?.id || f.personnel_id,
        visit_date: e.visit_date || f.visit_date,
        next_visit_date: e.next_visit_date || '',
        doctor_name: e.doctor_name || f.doctor_name,
        judgment: e.judgment || f.judgment,
      }));
      const parts = [];
      if (matched) parts.push(`dipendente ${matched.last_name} ${matched.first_name}`);
      if (e.visit_date) parts.push(`visita ${e.visit_date}`);
      if (e.judgment) parts.push(`giudizio "${e.judgment}"`);
      if (parts.length) toast.success(`✨ Auto-compilato: ${parts.join(' · ')}`);
      else toast.info('Documento letto. Verifica e completa i campi.');
    } catch {
      toast.warning('OCR fallito, compila manualmente');
    } finally {
      setOcrLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!form.file || !form.personnel_id || !form.visit_date) {
      toast.error('File, dipendente e data visita obbligatori');
      return;
    }
    try {
      setUploading(true);
      await medicalRecordsApi.upload(form.file, {
        personnel_id: form.personnel_id,
        visit_date: form.visit_date,
        next_visit_date: form.next_visit_date || undefined,
        doctor_name: form.doctor_name || undefined,
        judgment: form.judgment || undefined,
        notes: form.notes || undefined,
      });
      toast.success('Visita medica caricata');
      setShowUpload(false);
      setForm(initialForm);
      if (fileRef.current) fileRef.current.value = '';
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore upload');
    } finally {
      setUploading(false);
    }
  };

  const deleteRecord = async (r) => {
    if (!window.confirm(`Eliminare la visita di ${r.personnel_name}?`)) return;
    try {
      await medicalRecordsApi.delete(r.id);
      toast.success('Visita eliminata');
      load();
    } catch {
      toast.error('Errore eliminazione');
    }
  };

  const downloadReport = (format) =>
    window.open(reportsApi.getPersonnelSummaryUrl({ format }), '_blank');

  const personnelName = (p) => `${p.last_name || ''} ${p.first_name || ''}`.trim() || p.fiscal_code;

  return (
    <div className="space-y-6" data-testid="medical-records-page">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
        className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Stethoscope className="w-8 h-8 text-blue-600" />
            Scadenze Visite Mediche
          </h1>
          <p className="text-gray-600 mt-1">
            Carica i certificati di idoneità medica. L'AI estrae automaticamente i dati e calcola la scadenza.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => downloadReport('xlsx')}>
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Report Excel
          </Button>
          <Button variant="outline" onClick={() => downloadReport('pdf')}>
            <FileText className="w-4 h-4 mr-2" /> Report PDF
          </Button>
          <Button onClick={() => setShowUpload(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="upload-medical-btn">
            <Upload className="w-4 h-4 mr-2" /> Carica visita medica
          </Button>
        </div>
      </motion.div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="py-4">
          <div className="text-xs text-gray-500 uppercase">Totale</div>
          <div className="text-2xl font-bold mt-1">{stats.total}</div>
        </CardContent></Card>
        <Card className="border-emerald-200 bg-emerald-50/30"><CardContent className="py-4">
          <div className="text-xs text-emerald-700 uppercase flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Valide</div>
          <div className="text-2xl font-bold text-emerald-700 mt-1">{stats.valid}</div>
        </CardContent></Card>
        <Card className="border-amber-200 bg-amber-50/30"><CardContent className="py-4">
          <div className="text-xs text-amber-700 uppercase flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> In scadenza (60g)</div>
          <div className="text-2xl font-bold text-amber-700 mt-1">{stats.expiring}</div>
        </CardContent></Card>
        <Card className="border-red-200 bg-red-50/30"><CardContent className="py-4">
          <div className="text-xs text-red-700 uppercase flex items-center gap-1"><XCircle className="w-3 h-3" /> Scadute</div>
          <div className="text-2xl font-bold text-red-700 mt-1">{stats.expired}</div>
        </CardContent></Card>
      </div>

      <Card><CardContent className="py-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs">Cerca</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-3 text-gray-400" />
              <Input placeholder="Nome, CF, medico..." className="pl-8"
                value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>
          <div className="min-w-[160px]">
            <Label className="text-xs">Stato</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="valid">🟢 Valide</SelectItem>
                <SelectItem value="expiring_soon">🟡 In scadenza</SelectItem>
                <SelectItem value="expired">🔴 Scadute</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[220px]">
            <Label className="text-xs">Unità Locale</Label>
            <Select value={pdvFilter} onValueChange={setPdvFilter}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le UL</SelectItem>
                {pdvs.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name || p.company_name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent></Card>

      {loading ? <div className="text-center py-10 text-gray-500">Caricamento...</div>
        : filtered.length === 0 ? (
          <Card><CardContent className="py-16 text-center text-gray-500">
            <Stethoscope className="w-16 h-16 mx-auto mb-4 opacity-30" />
            {records.length === 0 ? "Nessuna visita caricata. Click 'Carica visita medica'." : 'Nessun risultato per i filtri.'}
          </CardContent></Card>
        ) : (
          <Card><CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Dipendente</TableHead>
                  <TableHead>Unità Locale</TableHead>
                  <TableHead>Data visita</TableHead>
                  <TableHead>Prossima visita</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead>Giudizio</TableHead>
                  <TableHead>Medico</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      <div className="font-medium">{r.personnel_name || '-'}</div>
                      <div className="text-xs text-gray-500 font-mono">{r.personnel_fiscal_code || ''}</div>
                    </TableCell>
                    <TableCell className="text-sm">{r.pdv_name || '-'}</TableCell>
                    <TableCell className="text-sm">{r.visit_date || '-'}</TableCell>
                    <TableCell className="text-sm">{r.next_visit_date || 'N/D'}</TableCell>
                    <TableCell>{statusBadge(r.status, r.days_to_expiry)}</TableCell>
                    <TableCell className="text-xs">{r.judgment || '-'}</TableCell>
                    <TableCell className="text-xs">{r.doctor_name || '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" title="Scarica"
                          onClick={() => window.open(medicalRecordsApi.getDownloadUrl(r.id), '_blank')}>
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button size="icon" variant="ghost" title="Elimina" onClick={() => deleteRecord(r)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        )}

      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Carica certificato visita medica</DialogTitle></DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4">
            <div className="space-y-1">
              <Label className="flex items-center gap-2">
                File visita * (PDF, JPG, PNG, DOCX - max 20MB)
                {ocrLoading && <span className="text-xs text-blue-600 flex items-center gap-1">
                  <Sparkles className="w-3 h-3 animate-pulse" /> Lettura automatica...
                </span>}
              </Label>
              <Input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.docx,.doc"
                onChange={(e) => handleFileChange(e.target.files?.[0] || null)} required />
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-blue-600" />
                I campi verranno compilati automaticamente leggendo il documento
              </p>
            </div>
            <div className="space-y-1">
              <Label>Dipendente *</Label>
              <Select value={form.personnel_id} onValueChange={(v) => setForm({ ...form, personnel_id: v })}>
                <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  {personnel.map((p) => (<SelectItem key={p.id} value={p.id}>
                    {personnelName(p)} {p.fiscal_code ? `(${p.fiscal_code})` : ''}
                  </SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Data visita *</Label>
                <Input type="date" value={form.visit_date}
                  onChange={(e) => setForm({ ...form, visit_date: e.target.value })} required />
              </div>
              <div className="space-y-1">
                <Label>Data prossima visita</Label>
                <Input type="date" value={form.next_visit_date}
                  onChange={(e) => setForm({ ...form, next_visit_date: e.target.value })}
                  placeholder="Auto-calcolato" />
                <p className="text-xs text-gray-500">Se vuota: + 1 anno dalla visita</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Medico competente</Label>
                <Input value={form.doctor_name}
                  onChange={(e) => setForm({ ...form, doctor_name: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Giudizio</Label>
                <Input value={form.judgment}
                  onChange={(e) => setForm({ ...form, judgment: e.target.value })}
                  placeholder="es. idoneo / con prescrizioni / non idoneo" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Note</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowUpload(false)}>Annulla</Button>
              <Button type="submit" disabled={uploading}>
                {uploading ? 'Caricamento...' : 'Carica visita'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
